import {
  InMemoryWebStorage,
  UserManager,
  WebStorageStateStore,
  type SigninRedirectArgs,
} from "oidc-client-ts";

import { getRuntimeConfig } from "@/runtimeConfig";
import { ADMIN_SCOPE } from "./jwt";

/** Les scopes d'une session ordinaire. */
export const DEFAULT_SCOPE = "openid email profile";

/**
 * La reconnexion qui débloque la page Security : elle demande, en plus des
 * scopes ordinaires, celui des opérations sur les passkeys.
 *
 * Elle n'impose PAS la réauthentification par elle-même : le Hosted UI classique
 * ignore `prompt=login` (doc AWS : réservé au managed login ; constaté en dev le
 * 2026-09-19). C'est la déconnexion qui la précède (page Security, voir
 * `auth/stepUp.ts`) qui la garantit : sans cookie de session, le formulaire est
 * inévitable. Ne JAMAIS lancer cette requête sans cette déconnexion : sur une
 * session ouverte, elle délivrerait le scope sans rien demander.
 *
 * Une session ordinaire ne porte jamais ce scope : un script injecté dans la
 * page ne peut donc pas enrôler sa propre clé sans provoquer cette déconnexion
 * visible, puis un formulaire qu'il ne sait pas remplir.
 */
export const STEP_UP_REQUEST: SigninRedirectArgs = {
  scope: `${DEFAULT_SCOPE} ${ADMIN_SCOPE}`,
  state: { returnTo: "/security" },
};

let manager: UserManager | null = null;

/**
 * Le `UserManager` unique de l'appli, partagé par ``AuthProvider`` et par la
 * connexion Touch ID (qui y injecte sa session).
 *
 * Le bootstrap (``main.tsx``) garantit que ``loadRuntimeConfig()`` est résolu
 * avant le premier appel : la config se lit de façon synchrone.
 */
export function getUserManager(): UserManager {
  if (manager) return manager;
  const cfg = getRuntimeConfig().cognito;
  const postLogoutRedirectUri = `${window.location.origin}/auth/logout`;
  const cognitoDomain = cfg.domain.replace(/\/$/, "");

  manager = new UserManager({
    authority: `https://cognito-idp.${cfg.region}.amazonaws.com/${cfg.userPoolId}`,
    metadataUrl: `https://cognito-idp.${cfg.region}.amazonaws.com/${cfg.userPoolId}/.well-known/openid-configuration`,
    client_id: cfg.userPoolClientId,
    redirect_uri: `${window.location.origin}/auth/callback`,
    post_logout_redirect_uri: postLogoutRedirectUri,
    response_type: "code",
    scope: DEFAULT_SCOPE,
    // Le jeton d'identité reste en mémoire seulement. Un XSS peut encore
    // s'exécuter sous notre origine, mais ne peut plus le lire dans
    // localStorage pour le rejouer depuis un hôte qu'il contrôle (que CORS
    // bloquerait de toute façon).
    userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
    // L'endpoint de déconnexion vit sur le domaine du Hosted UI, pas sous la
    // base IdP que rend la découverte OIDC : on surcharge les métadonnées pour
    // que signoutRedirect vise la bonne URL.
    metadataSeed: {
      end_session_endpoint: `${cognitoDomain}/logout?client_id=${cfg.userPoolClientId}&logout_uri=${encodeURIComponent(postLogoutRedirectUri)}`,
    },
  });
  return manager;
}
